<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import AudioPresentation from './AudioPresentation.vue';
import {
  getAudioAppearance,
  getAudioAvatarIdentity,
  getAudioGroupSenderName,
  isForwardedAudio,
} from './audioIdentity';
import { useMessageContext } from '../provider.js';
import {
  useDisplaySenderName,
  useGroupParticipantColor,
} from '../useDisplaySenderName.js';
import { useMapGetter } from 'dashboard/composables/store';
import { ORIENTATION } from '../constants';
import { isUploadedAudioAttachment } from 'dashboard/helper/audioAttachmentHelper';

const {
  attachments,
  sender,
  orientation,
  contentAttributes,
  conversationId,
  inboxId,
} = useMessageContext();

const conversationGetter = useMapGetter('getConversationById');
const inboxGetter = useMapGetter('inboxes/getInbox');

const attachment = computed(() => attachments.value[0]);
const isOutgoing = computed(() => orientation.value === ORIENTATION.RIGHT);
const isForwarded = computed(() => isForwardedAudio(contentAttributes.value));
const isUploadedAudio = computed(() =>
  isUploadedAudioAttachment({
    attachment: attachment.value,
    contentAttributes: contentAttributes.value,
  })
);

// Aceleres: em GRUPO o áudio precisa dizer quem gravou, como já fazem a
// figurinha e a foto — o `sender` da mensagem é o contato do grupo inteiro,
// então sem este rótulo cada áudio ficava anônimo dentro do grupo.
//
// A resolução é a MESMA de todas as outras bolhas (useDisplaySenderName), e
// não uma leitura própria de content_attributes: foi exatamente essa
// duplicação que fez o álbum de figurinhas exibir o autor errado. O que muda
// aqui é só ONDE o nome é desenhado — dentro do cartão do player, porque o
// BaseBubble do áudio é transparente e o rótulo dele flutuaria solto.
//
// O gate é conversa de grupo E mensagem recebida: fora do grupo, e no que o
// vendedor envia, o áudio continua exatamente como estava.
const conversation = computed(() =>
  conversationGetter.value(conversationId.value)
);
const displaySenderName = useDisplaySenderName();
const groupSenderColor = useGroupParticipantColor(displaySenderName);

const groupSenderName = computed(() =>
  getAudioGroupSenderName({
    displaySenderName: displaySenderName.value,
    conversation: conversation.value,
    isOutgoing: isOutgoing.value,
  })
);

const avatarIdentity = computed(() =>
  getAudioAvatarIdentity({
    contentAttributes: contentAttributes.value,
    sender: sender.value,
    inbox: inboxGetter.value(inboxId.value) || {},
    conversation: conversation.value,
    isOutgoing: isOutgoing.value,
    groupSenderName: groupSenderName.value,
  })
);

const avatarName = computed(() => avatarIdentity.value.name);
const avatarSrc = computed(() => avatarIdentity.value.src);
const audioAppearance = computed(() =>
  getAudioAppearance({
    contentAttributes: contentAttributes.value,
    isOutgoing: isOutgoing.value,
  })
);
</script>

<template>
  <BaseBubble
    class="relative bg-transparent"
    data-bubble-name="audio"
    hide-meta
    hide-sender-name
  >
    <AudioPresentation
      :attachment="attachment"
      :appearance="audioAppearance"
      :is-outgoing="isOutgoing"
      :avatar-name="avatarName"
      :avatar-src="avatarSrc"
      :sender-name="groupSenderName"
      :sender-color-class="groupSenderColor"
      :forwarded="isForwarded"
      :uploaded-audio="isUploadedAudio"
    />
  </BaseBubble>
</template>
